import { useEffect, useRef } from 'react';
import Konva from 'konva';
import { FieldTransform, FieldOrientation } from './FieldCanvas';
import { TilesetKey } from '../lib/bunkerCatalog';
import { fieldToStageCoords } from '../lib/fieldCoords';

interface StarterBrickLayerProps {
  stage: Konva.Stage;
  transform: FieldTransform;
  tileset: TilesetKey;
  orientation: FieldOrientation;
}

const W_FT = 16.20;
const D_FT = 5.10;

// Back edge sits at the field boundary (±75ft); center is inset by half the depth.
const CENTER_Z = 75 - D_FT / 2;  // 72.45ft

const PLACEMENTS = [
  { xFt: 0, zFt:  CENTER_Z, unityRotY:   0 },  // team 1
  { xFt: 0, zFt: -CENTER_Z, unityRotY: 180 },  // team 2
];

function canvasRot(unityY: number, orientation: FieldOrientation): number {
  const offset = orientation === 'horizontal' ? 270 : 180;
  return (unityY - offset + 360) % 360;
}

export const StarterBrickLayer: React.FC<StarterBrickLayerProps> = ({
  stage, transform, tileset, orientation,
}) => {
  const layerRef = useRef<Konva.Layer | null>(null);

  useEffect(() => {
    const layer = new Konva.Layer({ listening: false });
    layer.name('starterBricks');
    stage.add(layer);
    layerRef.current = layer;
    return () => { layer.destroy(); };
  }, [stage]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.destroyChildren();

    const htmlImg = new window.Image();
    htmlImg.onload = () => {
      if (!layerRef.current) return;
      const ppf    = transform.basePxPerFoot;
      const pxW    = W_FT * ppf;
      // Use natural aspect ratio so the image pixels aren't stretched.
      // Position uses the catalog D_FT independently of display height.
      const aspect = htmlImg.naturalWidth / htmlImg.naturalHeight;
      const pxH    = pxW / aspect;

      for (const p of PLACEMENTS) {
        const { x, y } = fieldToStageCoords(p.xFt, p.zFt, transform);
        const rot = canvasRot(p.unityRotY, orientation);
        const group = new Konva.Group({ x, y, rotation: rot, listening: false, opacity: 0.7 });
        group.add(new Konva.Image({
          image: htmlImg, x: -pxW / 2, y: -pxH / 2, width: pxW, height: pxH,
        }));
        layerRef.current.add(group);
      }
      layerRef.current.batchDraw();
    };
    htmlImg.src = `/sprites/${tileset}/STARTER_BRICK.png`;
  }, [transform, tileset, orientation]);

  return null;
};
